import type { ErrorInfo } from 'react';

export interface ErrorLog {
  id: string;
  timestamp: string;
  message: string;
  stack?: string;
  componentStack?: string;
  url: string;
  userAgent: string;
  userId?: string;
  sessionId: string;
  severity: 'error' | 'warning' | 'info';
  category: 'react' | 'network' | 'database' | 'auth' | 'rendering' | 'unknown';
  metadata?: Record<string, unknown>;
  handled: boolean;
  retryable?: boolean;
}

export interface PerformanceMetrics {
  errorId: string;
  memoryUsage?: number;
  connectionType?: string;
  onlineStatus: boolean;
  deviceInfo: {
    vendor?: string;
    model?: string;
    platform?: string;
  };
  appVersion?: string;
}

class ErrorLogger {
  private sessionId: string;
  private maxLogs = 50;

  constructor() {
    this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.initializeSessionInfo();
  }

  private initializeSessionInfo() {
    try {
      sessionStorage.setItem('sessionId', this.sessionId);

      // Clean old sessions on startup
      const keys = Object.keys(sessionStorage).filter((key) => key.startsWith('error_'));
      if (keys.length > this.maxLogs) {
        const oldKeys = keys.sort().slice(0, keys.length - this.maxLogs);
        oldKeys.forEach((key) => sessionStorage.removeItem(key));
      }
    } catch (error) {
      console.warn('[ErrorLogger] Failed to initialize session:', error);
    }
  }

  logError(
    error: Error,
    errorInfo?: ErrorInfo,
    additionalContext?: {
      category?: ErrorLog['category'];
      userId?: string;
      metadata?: Record<string, unknown>;
      retryable?: boolean;
    }
  ): ErrorLog {
    const errorLog: ErrorLog = {
      id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      message: error.message,
      stack: (error.stack as string | undefined) || undefined,
      componentStack: errorInfo?.componentStack || undefined,
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: additionalContext?.userId,
      sessionId: this.sessionId,
      severity: 'error',
      category: additionalContext?.category || 'unknown',
      metadata: additionalContext?.metadata,
      handled: !!errorInfo,
      retryable: additionalContext?.retryable,
    };

    this.storeErrorLog(errorLog);
    this.logToConsole(errorLog);

    return errorLog;
  }

  logWarning(
    message: string,
    category: ErrorLog['category'] = 'unknown',
    metadata?: Record<string, unknown>
  ) {
    const errorLog: ErrorLog = {
      id: `warn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      message,
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      severity: 'warning',
      category,
      metadata,
      handled: true,
    };

    this.storeErrorLog(errorLog);
    this.logToConsole(errorLog);
  }

  logInfo(
    message: string,
    category: ErrorLog['category'] = 'unknown',
    metadata?: Record<string, unknown>
  ) {
    const errorLog: ErrorLog = {
      id: `info_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString(),
      message,
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.sessionId,
      severity: 'info',
      category,
      metadata,
      handled: true,
    };

    this.storeErrorLog(errorLog);
    this.logToConsole(errorLog);
  }

  private storeErrorLog(errorLog: ErrorLog) {
    try {
      sessionStorage.setItem(errorLog.id, JSON.stringify(errorLog));
    } catch (error) {
      console.warn('[ErrorLogger] Failed to store error log:', error);
    }
  }

  private logToConsole(errorLog: ErrorLog) {
    const logMessage = `[${errorLog.severity.toUpperCase()}] [${errorLog.category}] ${errorLog.message}`;
    const logData = {
      id: errorLog.id,
      timestamp: errorLog.timestamp,
      url: errorLog.url,
      sessionId: errorLog.sessionId,
      stack: errorLog.stack,
      metadata: errorLog.metadata,
    };

    switch (errorLog.severity) {
      case 'error':
        console.error(logMessage, logData);
        break;
      case 'warning':
        console.warn(logMessage, logData);
        break;
      case 'info':
        console.info(logMessage, logData);
        break;
    }
  }

  getErrorLogs(): ErrorLog[] {
    try {
      const keys = Object.keys(sessionStorage).filter(
        (key) => key.startsWith('error_') || key.startsWith('warn_') || key.startsWith('info_')
      );

      return keys
        .map((key) => {
          const item = sessionStorage.getItem(key);
          return item ? (JSON.parse(item) as ErrorLog) : null;
        })
        .filter((log): log is ErrorLog => log !== null)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    } catch (error) {
      console.warn('[ErrorLogger] Failed to retrieve error logs:', error);
      return [];
    }
  }

  clearLogs() {
    try {
      const keys = Object.keys(sessionStorage).filter(
        (key) => key.startsWith('error_') || key.startsWith('warn_') || key.startsWith('info_')
      );
      keys.forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      console.warn('[ErrorLogger] Failed to clear error logs:', error);
    }
  }

  exportLogs(): string {
    const logs = this.getErrorLogs();
    const exportData = {
      exportTimestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      logs,
    };

    return JSON.stringify(exportData, null, 2);
  }

  getErrorStats(): {
    totalErrors: number;
    errorsByCategory: Record<string, number>;
    errorsBySeverity: Record<string, number>;
    recentErrors: ErrorLog[];
  } {
    const logs = this.getErrorLogs();
    const errorsByCategory: Record<string, number> = {};
    const errorsBySeverity: Record<string, number> = {};

    logs.forEach((log) => {
      errorsByCategory[log.category] = (errorsByCategory[log.category] || 0) + 1;
      errorsBySeverity[log.severity] = (errorsBySeverity[log.severity] || 0) + 1;
    });

    return {
      totalErrors: logs.length,
      errorsByCategory,
      errorsBySeverity,
      recentErrors: logs.slice(0, 10),
    };
  }

  capturePerformanceMetrics(errorId: string): PerformanceMetrics {
    const connectionApi = navigator as {
      connection?: { effectiveType?: string };
    };
    const userAgentDataApi = navigator as {
      userAgentData?: { platform?: string };
    };
    const vendorApi = navigator as { vendor?: string };

    const metrics: PerformanceMetrics = {
      errorId,
      onlineStatus: navigator.onLine,
      connectionType: connectionApi.connection?.effectiveType,
      deviceInfo: {
        vendor: vendorApi.vendor,
        model: userAgentDataApi.userAgentData?.platform || navigator.platform,
        platform: navigator.platform,
      },
    };

    if ('memory' in performance) {
      const memoryPerformance = performance as {
        memory: { usedJSHeapSize: number };
      };
      const memory = memoryPerformance.memory;
      metrics.memoryUsage = memory.usedJSHeapSize;
    }

    if (import.meta.env.VITE_APP_VERSION) {
      metrics.appVersion = import.meta.env.VITE_APP_VERSION;
    }

    try {
      sessionStorage.setItem(`metrics_${errorId}`, JSON.stringify(metrics));
    } catch (storageError) {
      console.warn('[ErrorLogger] Failed to store performance metrics:', storageError);
    }

    return metrics;
  }

  /**
   * Get GPU renderer info via WebGL - helps identify hardware differences
   */
  private getGpuInfo(): { vendor: string; renderer: string } | null {
    try {
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) return null;

      const webgl = gl as WebGLRenderingContext;
      const debugInfo = webgl.getExtension('WEBGL_debug_renderer_info');
      if (!debugInfo) return null;

      return {
        vendor: webgl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown',
        renderer: webgl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown',
      };
    } catch {
      return null;
    }
  }

  getDiagnosticsSummary(): {
    sessionInfo: {
      sessionId: string;
      startTime: string;
      url: string;
      userAgent: string;
    };
    errorStats: ReturnType<ErrorLogger['getErrorStats']>;
    systemInfo: {
      onlineStatus: boolean;
      cookieEnabled: boolean;
      language: string;
      timezone: string;
      screenResolution: string;
      colorDepth: number;
      // New hardware info
      devicePixelRatio: number;
      hardwareConcurrency: number | null;
      deviceMemory: number | null;
      maxTouchPoints: number;
      gpu: { vendor: string; renderer: string } | null;
      platform: string;
    };
  } {
    const sessionStartTime = sessionStorage.getItem('sessionStartTime') || new Date().toISOString();
    if (!sessionStorage.getItem('sessionStartTime')) {
      sessionStorage.setItem('sessionStartTime', sessionStartTime);
    }

    // Get hardware concurrency (CPU cores)
    const hardwareConcurrency =
      'hardwareConcurrency' in navigator ? (navigator.hardwareConcurrency as number) : null;

    // Get device memory (RAM in GB, Chrome only)
    const deviceMemory =
      'deviceMemory' in navigator
        ? ((navigator as { deviceMemory?: number }).deviceMemory ?? null)
        : null;

    return {
      sessionInfo: {
        sessionId: this.sessionId,
        startTime: sessionStartTime,
        url: window.location.href,
        userAgent: navigator.userAgent,
      },
      errorStats: this.getErrorStats(),
      systemInfo: {
        onlineStatus: navigator.onLine,
        cookieEnabled: navigator.cookieEnabled,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        colorDepth: window.screen.colorDepth,
        // New hardware info for debugging
        devicePixelRatio: window.devicePixelRatio,
        hardwareConcurrency,
        deviceMemory,
        maxTouchPoints: navigator.maxTouchPoints,
        gpu: this.getGpuInfo(),
        platform: navigator.platform,
      },
    };
  }
}

export const errorLogger = new ErrorLogger();
